import React, { useImperativeHandle, forwardRef, useState, useMemo } from 'react';
import ReportLayout from '../core/ReportLayout';
import { formatCurrency } from '../shared/utils';
import { exportToPDF, exportToExcel } from '../shared/exportUtils';
import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { successToast, errorToast } from '../../../lib/helpers';

const SixMonthSales = forwardRef(function SixMonthSales({ data, isLoading, searchTerm, hideHeader = false }, ref) {
    const [expandedAreas, setExpandedAreas] = useState(new Set());

    const groupedByArea = useMemo(() => {
        if (!data?.data || !Array.isArray(data.data) || !data.months) {
            return new Map();
        }

        const filteredData = searchTerm
            ? data.data.filter(customer =>
                customer.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.customerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.area?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : data.data;

        return filteredData.reduce((acc, customer) => {
            const area = customer.area || 'Unassigned';
            if (!acc.has(area)) {
                acc.set(area, {
                    customers: [],
                    totals: {
                        byMonth: Object.fromEntries((data.months || []).map(month => [month, 0])),
                        overall: 0
                    }
                });
            }

            const areaData = acc.get(area);
            areaData.customers.push(customer);

            if (customer.months) {
                Object.entries(customer.months).forEach(([month, value]) => {
                    if (areaData.totals.byMonth[month] !== undefined) {
                        areaData.totals.byMonth[month] += value || 0;
                    }
                });
            }
            areaData.totals.overall += customer.total || 0;

            return acc;
        }, new Map());
    }, [data, searchTerm]);

    // Calculate top 3 areas by sales
    const topAreas = useMemo(() => {
        const areasArray = Array.from(groupedByArea.entries())
            .map(([area, areaData]) => ({
                area,
                total: areaData.totals.overall,
                customerCount: areaData.customers.length
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 3);
        
        return areasArray;
    }, [groupedByArea]);

    // Calculate overall totals
    const overallTotals = useMemo(() => {
        let totalSales = 0;
        let totalCustomers = 0;
        Array.from(groupedByArea.values()).forEach(areaData => {
            totalSales += areaData.totals.overall;
            totalCustomers += areaData.customers.length;
        });
        return { totalSales, totalCustomers, areaCount: groupedByArea.size };
    }, [groupedByArea]);

    const toggleArea = (area) => {
        setExpandedAreas(prev => {
            const next = new Set(prev);
            if (next.has(area)) {
                next.delete(area);
            } else {
                next.add(area);
            }
            return next;
        });
    };

    const prepareExportData = () => {
        const tableData = [];
        Array.from(groupedByArea.entries()).forEach(([area, areaData]) => {
            areaData.customers.forEach(customer => {
                const row = {
                    area,
                    code: customer.customerCode || 'N/A',
                    customer: customer.customerName || 'N/A',
                    ...Object.fromEntries((data.months || []).map(month => [month, customer.months?.[month] || 0])),
                    total: customer.total || 0
                };
                tableData.push(row);
            });
        });

        return {
            tableData,
            columns: [
                { key: 'area', label: 'Area', format: 'text' },
                { key: 'code', label: 'Code', format: 'text' },
                { key: 'customer', label: 'Customer', format: 'text' },
                ...(data.months || []).map(month => ({ key: month, label: month, format: 'currency' })),
                { key: 'total', label: 'Total', format: 'currency' }
            ]
        };
    };

    const handleExportPDF = () => {
        try {
            const exportData = prepareExportData();
            exportToPDF({
                title: '6 Month Sales Report',
                subtitle: 'Sales by area and customer over 6 months',
                filename: `six-month-sales-${new Date().toISOString().split('T')[0]}.pdf`,
                columns: exportData.columns,
                tableData: exportData.tableData
            });
            successToast('PDF exported successfully');
        } catch (error) {
            console.error('PDF export failed:', error);
            errorToast('Failed to export PDF');
        }
    };

    const handleExportExcel = () => {
        try {
            const exportData = prepareExportData();
            exportToExcel(exportData.tableData, {
                title: '6 Month Sales Report',
                filename: `six-month-sales-${new Date().toISOString().split('T')[0]}.xlsx`,
                columns: exportData.columns
            });
            successToast('Excel file exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            errorToast('Failed to export Excel file');
        }
    };

    const handleEmailReport = () => {
        // TODO: Implement email functionality
        errorToast('Email functionality coming soon');
    };

    // Expose export handlers via ref
    useImperativeHandle(ref, () => ({
        handleExportPDF,
        handleExportExcel,
        handleEmailReport
    }));

    if (isLoading) {
        if (hideHeader) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            );
        }
        return (
            <ReportLayout
                title="6 Month Sales"
                subtitle="Sales by area and customer"
                loading={isLoading}
                icon={FileText}
                iconColor="purple"
            >
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            </ReportLayout>
        );
    }

    if (!data?.data || !data?.months) {
        if (hideHeader) {
            return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }
        return (
            <ReportLayout
                title="6 Month Sales"
                subtitle="Sales by area and customer"
                icon={FileText}
                iconColor="purple"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
            >
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            </ReportLayout>
        );
    }

    const content = (
        <div className="space-y-4">
            {/* Overall KPI Cards - Only show when not using hideHeader (standalone mode) */}
            {!hideHeader && overallTotals.totalSales > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.totalSales)}</div>
                        <div className="text-xs text-gray-500 mt-1">All Areas</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Areas</div>
                        <div className="text-2xl font-bold text-gray-900">{overallTotals.areaCount}</div>
                        <div className="text-xs text-gray-500 mt-1">Areas</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Total Customers</div>
                        <div className="text-2xl font-bold text-gray-900">{overallTotals.totalCustomers}</div>
                        <div className="text-xs text-gray-500 mt-1">Customers</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                        <div className="text-sm font-medium text-gray-600 mb-1">Avg per Area</div>
                        <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(overallTotals.areaCount > 0 ? overallTotals.totalSales / overallTotals.areaCount : 0)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Average</div>
                    </div>
                </div>
            )}

            {/* Top 3 Areas KPI Cards - Only show when not using hideHeader (standalone mode) */}
            {!hideHeader && topAreas.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 3 Areas by Sales</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {topAreas.map((areaData, index) => {
                            const colors = [
                                { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', text: 'text-yellow-700', textDark: 'text-yellow-900', textLight: 'text-yellow-600', badge: 'bg-yellow-500' },
                                { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', text: 'text-gray-700', textDark: 'text-gray-900', textLight: 'text-gray-600', badge: 'bg-gray-500' },
                                { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', textDark: 'text-amber-900', textLight: 'text-amber-600', badge: 'bg-amber-500' }
                            ];
                            const color = colors[index] || colors[0];
                            
                            return (
                                <div key={areaData.area} className={`bg-gradient-to-br ${color.bg} rounded-lg p-4 border ${color.border} hover:shadow-md transition-shadow`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className={`text-xs font-medium ${color.text} px-2 py-1 rounded ${color.badge} text-white`}>
                                            #{index + 1}
                                        </div>
                                        <div className={`text-xs ${color.textLight}`}>
                                            {areaData.customerCount} {areaData.customerCount === 1 ? 'customer' : 'customers'}
                                        </div>
                                    </div>
                                    <div className={`text-sm font-semibold ${color.textDark} mb-1`}>{areaData.area}</div>
                                    <div className={`text-2xl font-bold ${color.textDark}`}>{formatCurrency(areaData.total)}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Collapsible Area Sections */}
            {Array.from(groupedByArea.entries())
                .sort(([, a], [, b]) => b.totals.overall - a.totals.overall)
                .map(([area, areaData]) => {
                    const isExpanded = expandedAreas.has(area);
                    const areaIndex = Array.from(groupedByArea.keys()).sort((a, b) => {
                        const aTotal = groupedByArea.get(a)?.totals.overall || 0;
                        const bTotal = groupedByArea.get(b)?.totals.overall || 0;
                        return bTotal - aTotal;
                    }).indexOf(area);
                    const colorSchemes = [
                        { accent: 'border-l-4 border-l-blue-500', accentColor: 'blue' },
                        { accent: 'border-l-4 border-l-purple-500', accentColor: 'purple' },
                        { accent: 'border-l-4 border-l-green-500', accentColor: 'green' },
                        { accent: 'border-l-4 border-l-orange-500', accentColor: 'orange' },
                        { accent: 'border-l-4 border-l-red-500', accentColor: 'red' },
                        { accent: 'border-l-4 border-l-indigo-500', accentColor: 'indigo' }
                    ];
                    const colorScheme = colorSchemes[areaIndex % colorSchemes.length];

                    // Calculate average monthly sales
                    const avgMonthlySales = data.months.length > 0 
                        ? areaData.totals.overall / data.months.length 
                        : 0;

                    return (
                        <div key={area} className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${colorScheme.accent}`}>
                            {/* Header - Clickable to expand/collapse */}
                            <div 
                                className="p-4 border-b bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                onClick={() => toggleArea(area)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronUp className="h-5 w-5 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="h-5 w-5 text-gray-500" />
                                        )}
                                        <h3 className="text-lg font-semibold text-gray-900">{area}</h3>
                                        <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                            {areaData.customers.length} {areaData.customers.length === 1 ? 'customer' : 'customers'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-6 text-sm">
                                        <div className="text-right">
                                            <div className="text-gray-500">Total Sales</div>
                                            <div className="font-semibold text-gray-900">{formatCurrency(areaData.totals.overall)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Avg Monthly</div>
                                            <div className="font-semibold text-blue-600">{formatCurrency(avgMonthlySales)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-gray-500">Months</div>
                                            <div className="font-semibold text-purple-600">{data.months.length}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Collapsible Content */}
                            {isExpanded && (
                                <div className="p-4">
                                    {/* Area Summary KPI Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Total Sales</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(areaData.totals.overall)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Avg Monthly</div>
                                            <div className="text-lg font-bold text-gray-900">{formatCurrency(avgMonthlySales)}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Customers</div>
                                            <div className="text-lg font-bold text-gray-900">{areaData.customers.length}</div>
                                        </div>
                                        <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                                            <div className="text-xs font-medium text-gray-600 mb-1">Avg per Customer</div>
                                            <div className="text-lg font-bold text-gray-900">
                                                {formatCurrency(areaData.customers.length > 0 ? areaData.totals.overall / areaData.customers.length : 0)}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Monthly Summary Cards */}
                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Monthly Breakdown</h4>
                                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                            {data.months.map((month, monthIndex) => {
                                                const monthValue = areaData.totals.byMonth[month] || 0;
                                                const isHighest = monthValue === Math.max(...data.months.map(m => areaData.totals.byMonth[m] || 0));
                                                return (
                                                    <div key={month} className={`bg-white rounded-lg p-3 border border-gray-200 shadow-sm ${isHighest ? 'border-l-4 border-l-green-500' : ''}`}>
                                                        <div className="text-xs font-medium text-gray-600 mb-1">{month}</div>
                                                        <div className="text-lg font-bold text-gray-900">
                                                            {formatCurrency(monthValue)}
                                                        </div>
                                                        {isHighest && (
                                                            <div className="text-xs text-green-600 mt-1 font-medium">Highest</div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 border-2">
                                                <div className="text-xs font-medium text-gray-600 mb-1">Total</div>
                                                <div className="text-lg font-bold text-gray-900">
                                                    {formatCurrency(areaData.totals.overall)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Customer Table */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                                    {data.months.map(month => (
                                                        <th key={month} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            {month}
                                                        </th>
                                                    ))}
                                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {areaData.customers
                                                    .sort((a, b) => (b.total || 0) - (a.total || 0))
                                                    .map((customer) => (
                                                    <tr key={customer.customerCode} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{customer.customerCode}</td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{customer.customerName}</td>
                                                        {data.months.map(month => {
                                                            const value = customer.months?.[month] || 0;
                                                            return (
                                                                <td key={month} className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                                                                    {formatCurrency(value)}
                                                                </td>
                                                            );
                                                        })}
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                                                            {formatCurrency(customer.total || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                <tr className="bg-gray-50 font-bold">
                                                    <td className="px-4 py-3 whitespace-nowrap" colSpan={2}>
                                                        <span className="text-gray-900">Area Total</span>
                                                    </td>
                                                    {data.months.map(month => (
                                                        <td key={month} className="px-4 py-3 whitespace-nowrap text-right">
                                                            <span className="text-gray-900">
                                                                {formatCurrency(areaData.totals.byMonth[month] || 0)}
                                                            </span>
                                                        </td>
                                                    ))}
                                                    <td className="px-4 py-3 whitespace-nowrap text-right">
                                                        <span className="text-gray-900">
                                                            {formatCurrency(areaData.totals.overall)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
        </div>
    );

    if (hideHeader) {
        return <div>{content}</div>;
    }

    return (
        <ReportLayout
            title="6 Month Sales"
            subtitle="Sales by area and customer over 6 months"
            icon={FileText}
            iconColor="purple"
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onEmailReport={handleEmailReport}
        >
            {content}
        </ReportLayout>
    );
});

export default SixMonthSales;
